/**
 * DropdownField.tsx
 *
 * Unified dropdown/select field component with custom API support
 * Supports both single selection and multiple selection modes
 * Multi-select uses a custom dropdown with checkboxes for better UX
 */

import React, { useState, useEffect, useRef } from 'react';
import { BaseFieldProps, BASE_INPUT_CLASSES, ERROR_INPUT_CLASSES, DISABLED_INPUT_CLASSES } from './shared/types';
import { FieldWrapper } from './shared';
import { ChevronDown, X } from 'lucide-react';
import { API_BASE_URL } from '../../environments/environment';

export const DropdownField: React.FC<BaseFieldProps> = React.memo(({
  fieldConfig,
  mode,
  value,
  onChange,
  errors = [],
  disabled = false,
  hasError = false,
  formValues = {},
}) => {
  // Debounce timer for custom API calls
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isPreviewMode = mode === 'preview';
  const isDisabled = disabled || isPreviewMode;
  const [apiOptions, setApiOptions] = useState<Array<{ display: string; value: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  // Determine if we're in multi-select mode
  const allowMultiple = fieldConfig.allowMultiple === true;

  // State for custom multi-select dropdown
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Determine which options to use
  const hasCustomApi = fieldConfig.customApi?.enabled && fieldConfig.customApi?.url;
  const options = hasCustomApi ? apiOptions : (fieldConfig.options || []);

  // Parse the current value for multi-select
  const selectedValues: string[] = React.useMemo(() => {
    if (!allowMultiple) return [];
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      if (value.includes(',')) {
        return value.split(',').map(v => v.trim());
      }
      return value ? [value] : [];
    }
    return [];
  }, [value, allowMultiple]);

  // Close multi-select dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Extract a value from an object using dot notation path
  const extractValueFromObject = (obj: any, path: string): string => {
    if (!path) return JSON.stringify(obj);

    const parts = path.split('.');
    let result = obj;

    for (const part of parts) {
      if (result === null || result === undefined) return '';
      result = result[part];
    }

    return result !== null && result !== undefined ? String(result) : '';
  };

  // Extract data from response using enhanced dot notation with array indexing
  const extractDataFromResponse = (data: any, path: string): any[] => {
    if (!path) return Array.isArray(data) ? data : [data];

    const pathParts: Array<{ key: string; index?: number | '*' }> = [];
    const regex = /([^\[\].]+)|\[(\d+|\*)\]/g;
    let match;

    while ((match = regex.exec(path)) !== null) {
      if (match[1]) {
        pathParts.push({ key: match[1] });
      } else if (match[2]) {
        const index = match[2] === '*' ? '*' : parseInt(match[2], 10);
        pathParts.push({ key: '', index });
      }
    }

    let result: any = data;

    for (const part of pathParts) {
      if (!result) return [];

      if (part.index !== undefined) {
        if (!Array.isArray(result)) return [];

        if (part.index === '*') {
          continue;
        } else {
          result = result[part.index];
        }
      } else if (part.key) {
        if (typeof result === "object" && part.key in result) {
          result = result[part.key];
        } else if (Array.isArray(result)) {
          const prevPart = pathParts[pathParts.indexOf(part) - 1];
          if (prevPart?.index === '*') {
            result = result.map(item =>
              item && typeof item === "object" && part.key in item ? item[part.key] : null
            ).filter(item => item !== null);
          } else {
            return [];
          }
        } else {
          return [];
        }
      }
    }

    const flattenDeep = (arr: any[]): any[] => {
      return arr.reduce((acc, val) =>
        Array.isArray(val) ? acc.concat(flattenDeep(val)) : acc.concat(val),
        []
      );
    };

    return Array.isArray(result) ? flattenDeep(result) : [result];
  };

  const getDependencyFieldIds = (): string[] => {
    if (!fieldConfig.customApi?.pathParams) return [];
    return Object.values(fieldConfig.customApi.pathParams);
  };

  const getDependencyValues = (): string[] => {
    const depFieldIds = getDependencyFieldIds();
    return depFieldIds.map(fieldId => String(formValues[fieldId] || ''));
  };

  // Fetch options from custom API with 500ms debounce
  useEffect(() => {
    if (!hasCustomApi || mode === 'preview') return;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const fetchOptions = async () => {
      setLoading(true);
      setApiError(null);

      try {
        const config = fieldConfig.customApi!;

        const resolvePathParameters = (urlTemplate: string, pathParams: Record<string, string> | undefined): string | null => {
          if (!pathParams || Object.keys(pathParams).length === 0) {
            return urlTemplate;
          }

          let resolvedUrl = urlTemplate;

          Object.entries(pathParams).forEach(([placeholder, fieldId]) => {
            const fieldValue = formValues[fieldId];

            if (fieldValue === undefined || fieldValue === null || fieldValue === '') {
              return;
            }

            resolvedUrl = resolvedUrl.replace(`{${placeholder}}`, encodeURIComponent(String(fieldValue)));
          });

          if (resolvedUrl.includes('{')) {
            return null;
          }

          return resolvedUrl;
        };

        const resolveQueryParams = (params: Record<string, string> | undefined): Record<string, string> => {
          if (!params) return {};

          const resolved: Record<string, string> = {};
          Object.entries(params).forEach(([key, val]) => {
            if (typeof val === 'string' && val.startsWith('{') && val.endsWith('}')) {
              const fieldId = val.slice(1, -1);
              const fieldValue = formValues[fieldId];

              if (fieldValue === undefined || fieldValue === null || fieldValue === '') {
                console.log(`Field reference ${val} not available (field value is empty)`);
                return;
              }

              console.log(`Resolved field reference ${val} to value: ${fieldValue}`);
              resolved[key] = String(fieldValue);
            } else {
              resolved[key] = val;
            }
          });
          return resolved;
        };

        const resolvedUrlPath = resolvePathParameters(config.url, config.pathParams);
        if (!resolvedUrlPath) {
          setApiOptions([]);
          setLoading(false);
          return;
        }

        const resolvedParams = resolveQueryParams(config.queryParams);

        const headers: Record<string, string> = {
          ...(config.headers || {}),
        };

        console.log('Fetching dropdown options via backend proxy from:', resolvedUrlPath);

        const proxyUrl = `${API_BASE_URL}/api/proxy/external-api`;
        const response = await fetch(proxyUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            url: resolvedUrlPath,
            method: config.method || 'GET',
            params: Object.keys(resolvedParams).length > 0 ? resolvedParams : undefined,
            headers: Object.keys(headers).length > 0 ? headers : undefined,
            body: config.method !== 'GET' && config.body ? config.body : undefined,
          }),
        });

        const data = await response.json();

        if (response.ok) {
          const extractedData = extractDataFromResponse(data, config.responsePath || "");

          const optionPairs = extractedData.map((item: any) => {
            const display = config.displayValuePath
              ? extractValueFromObject(item, config.displayValuePath)
              : typeof item === "string" ? item : JSON.stringify(item);

            const value = config.valuePath
              ? extractValueFromObject(item, config.valuePath)
              : typeof item === "string" ? item : JSON.stringify(item);

            return { display, value };
          });

          setApiOptions(optionPairs);
          setApiError(null);
          console.log(`Successfully fetched ${optionPairs.length} options`);
        } else {
          setApiError(`Failed to fetch options: ${response.status} ${response.statusText}`);
          console.error("API error:", response.status, response.statusText, data);
        }
      } catch (error: any) {
        setApiError(`Error: ${error.message}`);
        console.error("Error fetching options:", error);
      } finally {
        setLoading(false);
      }
    };

    debounceTimerRef.current = setTimeout(() => {
      fetchOptions();
    }, 500);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [
    hasCustomApi,
    fieldConfig.customApi?.url,
    fieldConfig.customApi?.method,
    fieldConfig.customApi?.responsePath,
    fieldConfig.customApi?.displayValuePath,
    fieldConfig.customApi?.valuePath,
    JSON.stringify(fieldConfig.customApi?.pathParams),
    JSON.stringify(fieldConfig.customApi?.queryParams),
    JSON.stringify(fieldConfig.customApi?.headers),
    fieldConfig.customApi?.body,
    JSON.stringify(getDependencyValues()),
    mode,
  ]);

  // Check if this field has unresolved dependencies
  const dependencyFieldIds = getDependencyFieldIds();
  const unresolvedDependencies = dependencyFieldIds.filter(depId =>
    !formValues[depId] || formValues[depId] === ''
  );
  const hasMissingDependencies = unresolvedDependencies.length > 0;

  const selectClasses = `${BASE_INPUT_CLASSES} ${hasError ? ERROR_INPUT_CLASSES : ''} ${isDisabled ? DISABLED_INPUT_CLASSES : ''}`;

  // --- Multi-select handlers ---
  const handleMultiSelectToggle = (optionValue: string) => {
    if (isDisabled || loading || hasMissingDependencies) return;

    const newValues = selectedValues.includes(optionValue)
      ? selectedValues.filter(v => v !== optionValue)
      : [...selectedValues, optionValue];

    onChange(fieldConfig.id, newValues);
  };

  const handleRemoveTag = (optionValue: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (isDisabled) return;

    const newValues = selectedValues.filter(v => v !== optionValue);
    onChange(fieldConfig.id, newValues);
  };

  // --- Single-select handler ---
  const handleSingleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (isPreviewMode) return;
    onChange(fieldConfig.id, e.target.value);
  };

  // Normalize value to a string for single-select — guards against a previously
  // stored array (e.g. when allowMultiple was true) being passed to <select>.
  const singleSelectValue = Array.isArray(value) ? (value[0] ?? '') : (value || '');

  // Render multi-select dropdown
  if (allowMultiple) {
    return (
      <FieldWrapper
        field={fieldConfig}
        errors={errors}
        hasError={hasError}
        isPreview={isPreviewMode}
      >
        <div ref={dropdownRef} className="relative">
          <button
            type="button"
            onClick={() => !isDisabled && setIsOpen(!isOpen)}
            disabled={isDisabled || loading || hasMissingDependencies}
            className={`${selectClasses} flex items-center justify-between min-h-[42px] text-left`}
            style={{ paddingTop: selectedValues.length > 0 ? '6px' : '8px' }}
          >
            <div className="flex-1 flex flex-wrap gap-1">
              {selectedValues.length === 0 ? (
                <span className="text-gray-400">
                  {loading ? 'Loading options...' : hasMissingDependencies ? 'Fill dependency fields' : 'Select options...'}
                </span>
              ) : (
                selectedValues.map((val, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs"
                  >
                    {val}
                    {!isDisabled && (
                      <X
                        className="h-3 w-3 cursor-pointer hover:text-blue-900"
                        onClick={(e) => handleRemoveTag(val, e)}
                      />
                    )}
                  </span>
                ))
              )}
            </div>
            <ChevronDown className={`h-4 w-4 ml-2 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>

          {isOpen && !isDisabled && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
              {options.map((option: any, idx: number) => {
                const optionValue = typeof option === 'string' ? option : option.value;
                const optionDisplay = typeof option === 'string' ? option : option.display;
                const isSelected = selectedValues.includes(optionValue);
                return (
                  <div
                    key={idx}
                    className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleMultiSelectToggle(optionValue)}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{optionDisplay}</span>
                  </div>
                );
              })}
              {options.length === 0 && (
                <div className="px-3 py-2 text-sm text-gray-500">No options available</div>
              )}
            </div>
          )}
        </div>
      </FieldWrapper>
    );
  }

  // Render single-select dropdown
  return (
    <FieldWrapper
      field={fieldConfig}
      errors={errors}
      hasError={hasError}
      isPreview={isPreviewMode}
    >
      <>
        {apiError && mode !== 'preview' && (
          <div className="text-xs text-red-500 mb-2">
            {apiError}
          </div>
        )}
        {hasCustomApi && hasMissingDependencies && !loading && !isPreviewMode && (
          <div className="text-xs text-amber-600 mb-2">
            Please fill in the required fields first
          </div>
        )}
        <select
          id={fieldConfig.id}
          value={singleSelectValue}
          onChange={handleSingleChange}
          onFocus={(e) => {
            if (!isPreviewMode) {
              e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }}
          disabled={isDisabled || loading || hasMissingDependencies}
          className={`${selectClasses} ${!singleSelectValue ? 'text-gray-400' : ''}`}
        >
          <option value="">{loading ? 'Loading options...' : hasMissingDependencies ? 'Fill dependency fields' : 'Select an option'}</option>
          {options.map((option: any, idx: number) => {
            const optionValue = typeof option === 'string' ? option : option.value;
            const optionDisplay = typeof option === 'string' ? option : option.display;

            return (
              <option key={idx} value={optionValue} className="text-black">
                {optionDisplay}
              </option>
            );
          })}
        </select>
      </>
    </FieldWrapper>
  );
});

DropdownField.displayName = 'DropdownField';
